import type {
  DataTableGroupBy,
  FilterOption,
  SelectOption,
} from "@playbookai/ui";
import {
  daysBefore,
  isoDateInZone,
  previousDay,
  startOfDayInZone,
  type DateTimeFormatter,
} from "../../../settings/format-date-time.js";
import type {
  AuditActorOption,
  AuditSearchFilters,
} from "../../../api/admin-audit-api.js";

/** Filters as the form holds them. The text fields are strings because they
    come from inputs, and empty strings are dropped by the API client.
    `includeRoutine` is the one boolean: the switch that brings routine
    activity back into the list (PB-5410). */
export interface FilterDraft {
  service: string;
  action: string;
  actorId: string;
  from: string;
  to: string;
  includeRoutine: boolean;
}

export const EMPTY_FILTERS: FilterDraft = {
  service: "",
  action: "",
  actorId: "",
  from: "",
  to: "",
  includeRoutine: false,
};

/** A calendar day (`2026-08-12`) as an instant, in the READER's own zone.
 *
 * The filter is by DAY, not by minute: an administrator narrowing a compliance
 * log thinks "the 12th", never "13:04 on the 12th". `edge` decides which end of
 * that day is meant — `from` opens it at 00:00 and `to` closes it at the last
 * millisecond before the next day begins, so a single day chosen at both ends
 * selects that whole day rather than an empty instant-to-itself window.
 *
 * The zone is the one the LIST renders in (PB-5216 d7), not the host's. It was
 * host-local, which was consistent while the rows were host-local too; once
 * the times moved to the Region preference's zone, a reader whose Region
 * differs from their browser would pick "the 12th" and receive a window
 * running from their BROWSER's midnight — hours of the 11th, and none of the
 * 12th's tail.
 *
 * The end of a day is the start of the NEXT one minus a millisecond, never
 * "the same day at 23:59:59.999": on a day a DST transition made 23 or 25
 * hours long those are different instants. `startOfDayInZone` also rejects a
 * day that does not exist (2026-02-30), which `new Date(...)` rolls silently
 * forward into March.
 */
function toInstant(
  day: string,
  edge: "start" | "end",
  timeZone: string,
): string | undefined {
  const start = startOfDayInZone(day, timeZone);
  if (start === null) return undefined;
  if (edge === "start") return new Date(start).toISOString();
  // 36 hours lands inside the next calendar day whatever the transition did
  // to this one's length; its own start is then the exclusive upper bound.
  const nextDay = isoDateInZone(start + 36 * 60 * 60 * 1000, timeZone);
  const nextStart = startOfDayInZone(nextDay, timeZone);
  return nextStart === null ? undefined : new Date(nextStart - 1).toISOString();
}

export function toApiFilters(
  draft: FilterDraft,
  timeZone: string,
): AuditSearchFilters {
  const from = toInstant(draft.from, "start", timeZone);
  const to = toInstant(draft.to, "end", timeZone);
  return {
    service: draft.service,
    action: draft.action,
    actorId: draft.actorId,
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    /* Always sent, both ways: the server's default is the complete record, so
       leaving it out when the switch is on would still ask for the whole record
       — but only by accident. Saying so explicitly keeps the request, the query
       key and the export job describing the view the reader is looking at. */
    hideRoutine: draft.includeRoutine ? "false" : "true",
  };
}

/** Is anything NARROWING the record, the switch aside?
 *
 * Kept apart from the switch on purpose. The empty state asks "did a filter
 * exclude everything?", and a reader who only turned routine activity ON has
 * narrowed nothing — telling them "No events match these filters" would be a
 * false claim about what they asked for. */
export function isNarrowed(filters: FilterDraft): boolean {
  return [
    filters.service,
    filters.action,
    filters.actorId,
    filters.from,
    filters.to,
  ].some((value) => value !== "");
}

/** The day groups (PB-5216, d7).
 *
 * A row is keyed on the CALENDAR DATE of the event in the reader's zone
 * (`YYYY-MM-DD`), so a group is one day as that reader experiences it — never
 * the host's `getDate()`, which answers in a different zone the moment a
 * Region preference is saved. Today and Yesterday are named by comparing that
 * key with today's, and with the calendar day before it.
 *
 * Yesterday is CALENDAR arithmetic, not "now − 24 hours": on the day after the
 * clocks go forward, a fixed 24-hour step lands two days back for the first
 * hour of the day, which would leave the real yesterday unlabelled and call
 * the day before it "Yesterday".
 *
 * The header shows the readable date; the key stays the machine one. Rows
 * arrive newest first, so the runs are already contiguous. */
export function dayGroups(
  formatter: DateTimeFormatter,
): DataTableGroupBy<{ ts: string }> {
  return {
    key: (row) => formatter.isoDate(row.ts) || row.ts,
    label: (key, rows) => {
      const readable = rows[0] ? formatter.date(rows[0].ts) || key : key;
      const today = formatter.isoDate(Date.now());
      if (key === today) return `Today · ${readable}`;
      if (key === previousDay(today)) return `Yesterday · ${readable}`;
      return readable;
    },
  };
}

/** Today as `YYYY-MM-DD` in the zone the page renders in.
 *
 * Not `toISOString().slice(0, 10)` — that yields the UTC date, which is
 * already tomorrow for anyone far enough east and would cap the calendar a day
 * short of what they can legitimately pick — and not the host's date either,
 * which is a different day from the rendered one whenever the reader has saved
 * a Region preference. */
export function todayInZone(timeZone: string): string {
  return isoDateInZone(Date.now(), timeZone);
}

/** How far back the From field reaches when a service or action filter fills
 * it. The name predates the Action filter (PB-5410); both are the unindexed
 * pair, so both take the same window.
 *
 * `service` is one of the two filters `audit_log` does not index, so the API
 * refuses it without a lower time bound — unbounded it reads the tenant's
 * whole history, and the cost grows every month for ever (PB-5142). The form
 * supplies the bound rather than the server defaulting one silently, because a
 * row missing from a narrowed compliance answer reads as "that service did
 * nothing"; here the window is in the field, where a reader can see it and
 * widen it.
 *
 * 90 days because it is a review quarter, and because cost scales with the
 * monthly partitions the window spans: ~4,400 buffers at 90 days against
 * ~23,400 unbounded. */
export const SERVICE_FILTER_DEFAULT_DAYS = 90;

/** The calendar day `days` before today, in the reader's own zone.
 *
 * CALENDAR arithmetic on the zone's own date, not `Date.now()` minus
 * `days × 24h`. Subtracting fixed 24-hour periods is a day out whenever the
 * window crosses a spring-forward and the local clock is within the DST delta
 * of midnight: at 00:30 on 2026-03-09 in America/New_York (the day after that
 * zone's transition) it yields 2025-12-08, an 89-day window, where the reader
 * asked for 90 days back — 2025-12-09. Shifting the DAY never has that
 * problem, because a calendar day has no length.
 *
 * The arithmetic itself is `daysBefore` in `format-date-time.ts`, which is
 * where this file already gets `previousDay` — the same parse, the same
 * normalisation and the same "a calendar day has no length" reasoning. An
 * earlier revision inlined a private copy here, along with a second ISO-day
 * regex; one parse and one explanation is worth more than the indirection it
 * costs. All this adds is "today, in the reader's zone" as the starting point.
 *
 * Counted back from `anchor` when the reader has already chosen an upper
 * bound, and from today otherwise. Counting from today regardless would hand
 * `from` a day LATER than a historical `to` — the page inventing a reversed
 * range that `rangeError` then blocks Apply and Export on, and that the From
 * picker's own `max={draft.to || today}` forbids the reader to type by hand.
 * Anchoring on `to` cannot do that: 90 days before a day is never after it.
 *
 * `todayInZone` always yields `YYYY-MM-DD` — `isoDateInZone` pins a Gregorian,
 * Latin-digit locale so that it does, rather than inheriting the reader's
 * calendar and numerals — so `daysBefore` cannot take its malformed-input
 * branch from here. That branch returns "" — which as a `from` value would be
 * the UNBOUNDED request this pre-fill exists to prevent, so it would fail open
 * into the very 400 the field is filling in to avoid. It is unreachable rather
 * than guarded against, and is written down because the failure direction is
 * the surprising part. */
export function daysAgoInZone(
  days: number,
  timeZone: string,
  anchor?: string,
): string {
  return daysBefore(anchor || todayInZone(timeZone), days);
}

export const PAGE_SIZE = 25;

/**
 * Is this range refusable before it reaches the server?
 *
 * Extracted rather than left inline so the rule can be asserted directly,
 * without mounting a calendar and driving it.
 *
 * Both values are `YYYY-MM-DD`, which compares correctly as a string for that
 * fixed-width form — and unlike `new Date(...)` it cannot be thrown off by a
 * zone. The picker already refuses out-of-order days; this catches a range that
 * became invalid because the OTHER end moved after it was chosen.
 */
export function rangeError(from: string, to: string): string | null {
  if (from && to && from > to) {
    return "The start of the range must not be after the end.";
  }
  return null;
}

/**
 * Is this filter set refusable before it reaches the server?
 *
 * The sibling of `rangeError`, and here for the reason that one states: the
 * server rejects this too, and saying so here is kinder than surfacing its
 * refusal. PB-5142 requires a lower bound alongside `service` and `action` —
 * the two filters `audit_log` does not index — and without this check the
 * request goes out unbounded and the reader is shown the API's own words,
 * which say "unindexed", which is not an administrator's problem.
 *
 * Reachable in one step: choose a service or an action, which fills `From`
 * with 90 days, then clear `From` because you want all time, then Apply.
 *
 * Both, since PB-5410 gave the page an Action control: this condition is where
 * that note said the control belonged "on the same day". Names exactly the
 * filters that need the bound, so the reader knows which one to clear.
 */
export function unboundedFilterError(
  filters: Pick<FilterDraft, "service" | "action" | "from">,
): string | null {
  if (filters.from) return null;
  const unbounded = [
    filters.service ? "service" : null,
    filters.action ? "action" : null,
  ].filter((name): name is string => name !== null);
  if (unbounded.length === 0) return null;
  const names = unbounded.join(" and ");
  const both = unbounded.length > 1;
  return `Filtering by ${names} ${both ? "need" : "needs"} a start date — pick one, or clear the ${names} ${both ? "filters" : "filter"} to search all time.`;
}

/**
 * Should the page already on screen stay while the next one loads?
 *
 * Only while the TENANT and the FILTERS both match. Matching on tenant alone
 * let a filter change keep the previous rows visible during the new fetch, and
 * the table shows its loading state only on a FIRST fetch — so those rows sat
 * there with nothing to say they answered a different question.
 *
 * Cursor changes within one filter set still reuse the page, which is the
 * whole point of the placeholder.
 *
 * Extracted rather than left inline in the query so the rule can be asserted
 * without driving a table through two fetches.
 */
export function keepsPreviousPage(
  previousKey: readonly unknown[] | undefined,
  tenantId: string | null | undefined,
  filters: AuditSearchFilters,
): boolean {
  /* `auditQueryKeys.search` stores `tenantId ?? "none"` at index 1, and the
     page's own `tenantId` comes from an optional claim — so compare the
     NORMALISED value, or a page with no tenant never matches its own key and
     silently loses placeholder retention. */
  if (previousKey?.[1] !== (tenantId ?? "none")) return false;
  return JSON.stringify(previousKey?.[3]) === JSON.stringify(filters);
}

/** Build select options from a facet list, with "Any" as the cleared state.
    An unloaded list still yields "Any", so the control is never an empty
    select that looks broken while the facets are in flight.

    Ordered by the VISIBLE label, not by the stored id the API returns them in.
    Sorted by id, "Sign-in & identity" (`auth`) came third — ahead of
    "Collaboration" and "Dashboard" — which reads as unsorted to someone
    scanning the words on screen (PB-5243). "Any" is prepended AFTER the sort:
    it is the cleared state rather than a service, so it belongs at the top and
    not filed under "A". */
export function optionsFrom(
  values: readonly string[] | undefined,
  label: (value: string) => string,
): SelectOption[] {
  return [
    { value: "", label: "Any" },
    ...(values ?? [])
      .map((value) => ({ value, label: label(value) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
}

/** The Actor options: people by NAME, as person rows (PB-5410).
    `optionsFrom` takes plain values; an actor is an {id, name} pair whose id
    is the filter's wire value and must never be what the reader sees, so the
    mapping lives here, once. Former members arrive already named by their
    reference, so they stay selectable — the record outlives membership. "Any"
    is the actor menu's own clear, so it is not an option here. */
export function actorOptionsFrom(
  actors: readonly AuditActorOption[] | undefined,
): FilterOption[] {
  return (actors ?? [])
    .map((actor) => ({ value: actor.id, label: actor.name, person: {} }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Say how many options there are, or why there are none. A picker offering
    only "Any" with no explanation reads as broken rather than as empty. */
export function facetHint(
  count: number | undefined,
  noun: string,
  failed = false,
  truncated = false,
): string {
  /* A failed facets request left `count` undefined, which read as
     "Loading…" forever — the picker looked like it was still working while it
     had already given up, and an admin waits instead of retrying. The list is
     still usable (every filter accepts a typed value), so this says the
     choices are missing rather than blocking the page. */
  if (failed) return `Could not load ${noun}`;
  if (count === undefined) return "Loading\u2026";
  if (count === 0) return `No ${noun} recorded yet`;
  /* A capped list must not quote its count as if it were the total. "500
     people in this workspace" beside a truncated list is a false statement on
     a compliance filter, and an absent option then reads as "that person did
     nothing" rather than "the list stops here". */
  if (truncated) return `First ${count} ${noun} — more exist`;
  return `${count} ${noun} in this workspace`;
}

export type ExportState =
  | { status: "idle" }
  | { status: "working" }
  /* `routineExcluded` because the CSV does not list its own filters: without
     a word here a default-view export could pass for the complete record. */
  | { status: "queued"; routineExcluded: boolean }
  | { status: "failed"; message: string };
